import { css } from "@emotion/css";
import { useEffect, useRef, useState } from "react";

import {
  fetchPdfPageCount,
  getReportAttachmentPdfHeight,
} from "./getPdfPageCount";
import {
  REPORT_ATTACHMENT_PDF_MIN_HEIGHT,
  REPORT_ATTACHMENT_PDF_VIEWER_HASH,
  REPORT_ATTACHMENT_PDF_VIEWER_HASH_SCROLLABLE,
} from "../../constants";

type SummaryOfRightsPdfProps = {
  url: string;
  title: string;
};

/**
 * Read-only SuperDoc PDF in the report card, matching SummaryOfRightsEsign.
 * Chrome's PDF iframe is a nested scroller; size it to the document height so
 * the report page scrolls instead.
 */
export function SummaryOfRightsPdf({ url, title }: SummaryOfRightsPdfProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [isPageCountResolved, setIsPageCountResolved] = useState(false);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setPageCount(null);
    setIsPageCountResolved(false);

    fetchPdfPageCount(url).then((count) => {
      if (!cancelled) {
        setPageCount(count);
        setIsPageCountResolved(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;

    const updateWidth = () => {
      setWidth(root.clientWidth);
    };

    updateWidth();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateWidth);
    resizeObserver?.observe(root);

    return () => {
      resizeObserver?.disconnect();
    };
  }, []);

  const hasKnownPageCount = pageCount !== null;
  const height =
    hasKnownPageCount && pageCount !== null
      ? getReportAttachmentPdfHeight(width, pageCount)
      : 0;
  const viewerHash = hasKnownPageCount
    ? REPORT_ATTACHMENT_PDF_VIEWER_HASH
    : REPORT_ATTACHMENT_PDF_VIEWER_HASH_SCROLLABLE;

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      {isPageCountResolved && (
        <iframe
          src={`${url}#${viewerHash}`}
          title={title}
          data-testid="summary-of-rights-pdf"
          className={
            hasKnownPageCount ? styles.pdf : styles.pdfWithFallbackHeight
          }
          style={height > 0 ? { height: `${height}px` } : undefined}
        />
      )}
    </div>
  );
}

const styles = {
  wrapper: css({
    width: "100%",
  }),
  pdf: css({
    width: "100%",
    border: "none",
    display: "block",
  }),
  pdfWithFallbackHeight: css({
    width: "100%",
    minHeight: REPORT_ATTACHMENT_PDF_MIN_HEIGHT,
    border: "none",
    display: "block",
  }),
};
